import {
  AppContextType,
  AppUninstallableReason,
  UnifiedDetailsFeature,
} from 'marketplace-ui-common/types';
import { useMemo } from 'react';
import { useInstallability } from '../../hooks/useInstallability';

interface UseAppUninstallableReasonArgs {
  appContext: AppContextType;
  listingId: number;
  features: UnifiedDetailsFeature[];
  published: boolean;
  isMcpServer?: boolean;
  hasMcpServerAccess?: boolean;
  skip?: boolean;
}

type UseAppUninstallableReasonReturn = {
  uninstallableReason: AppUninstallableReason | undefined;
  missingScopeGroups: string[];
  missingScopes: string[];
  loading: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
};

export function useAppUninstallableReason({
  appContext,
  listingId,
  features: __features,
  published,
  isMcpServer,
  hasMcpServerAccess,
  skip,
}: UseAppUninstallableReasonArgs): UseAppUninstallableReasonReturn {
  const {
    data: installabilityData,
    loading: installabilityLoading,
    error: installabilityError,
    refetch: refetchInstallability,
  } = useInstallability({
    listingId,
    skip: skip || !listingId,
    published,
    appContext,
  });

  const uninstallableReason: AppUninstallableReason | undefined =
    useMemo(() => {
      if (!installabilityData) return undefined;

      if (installabilityData.installed) {
        return { label: 'ALREADY_INSTALLED' };
      }

      // Check MCP server access - portal doesn't have access to this MCP server
      if (isMcpServer && !hasMcpServerAccess) {
        return { label: 'MCP_SERVER_UNAVAILABLE' };
      }

      const uninstallableReasonData =
        installabilityData.installability?.uninstallableReason;
      const portalInstallable =
        installabilityData.installability?.portalInstallable;
      const userInstallable =
        installabilityData.installability?.userInstallable;
      const missingUserRoles =
        // Field expected at the same level as installability in the return payload
        // Fallback to empty array if not present in the current schema
        installabilityData?.derivedMissingUserRoles ?? [];
      const productSpecific =
        uninstallableReasonData?.productSpecificUninstallableReason;
      const appSpecific =
        productSpecific?.['@type'] === 'app' ? productSpecific : undefined;

      // Portal must enable sensitive data before this app can be installed
      if (appSpecific?.sensitiveDataRequired) {
        return { label: 'SENSITIVE_DATA_REQUIRED' };
      }

      const missingPortalScopes =
        uninstallableReasonData?.missingPortalScopes ?? [];
      const missingUserScopes =
        uninstallableReasonData?.missingUserScopes ?? [];
      const productSpecificMissingPortalScopes =
        appSpecific?.missingPortalInternalScopes?.flat() ?? [];
      const productSpecificMissingUserScopes =
        appSpecific?.missingUserInternalScopes?.flat() ?? [];
      const missingPortalScopeGroups =
        appSpecific?.missingPortalScopeGroups ?? [];
      const missingUserScopeGroups = appSpecific?.missingUserScopeGroups ?? [];

      // MISSING_HUB_SCOPES if the app is not installable at the portal level based on scopes
      if (
        missingPortalScopes.length ||
        missingPortalScopeGroups.length ||
        productSpecificMissingPortalScopes.length
      ) {
        return {
          label: 'MISSING_HUB_SCOPES',
          missingScopes: [
            ...missingPortalScopes,
            ...missingPortalScopeGroups,
            ...productSpecificMissingPortalScopes,
          ],
        };
      }

      // MISSING_USER_PERMISSIONS if the app is installable at the portal level but not at the user level based on scopes
      if (
        portalInstallable &&
        (missingUserScopes.length ||
          missingUserScopeGroups.length ||
          productSpecificMissingUserScopes.length)
      ) {
        return { label: 'MISSING_USER_PERMISSIONS', missingUserRoles };
      }

      const incompatibleCombined =
        ((appSpecific?.incompatiblePortalInstalledAppIds ?? []).length
          ? appSpecific?.incompatiblePortalInstalledAppIds
          : appSpecific?.incompatibleUserInstalledAppIds) ?? [];
      if (incompatibleCombined.length) {
        return {
          label: 'INCOMPATIBLE_INSTALLS',
          incompatibleInstalls: incompatibleCombined as unknown as string[],
        };
      }

      if (!portalInstallable && !userInstallable)
        return { label: 'UNKNOWN_UNINSTALLABLE_REASON' };
    }, [installabilityData, isMcpServer, hasMcpServerAccess]);

  const missingScopes = useMemo(() => {
    const productSpecific =
      installabilityData?.installability?.uninstallableReason
        ?.productSpecificUninstallableReason;
    const appSpecific =
      productSpecific?.['@type'] === 'app' ? productSpecific : undefined;
    const missingUserScopes =
      installabilityData?.installability?.uninstallableReason
        ?.missingUserScopes ?? [];
    const missingPortalScopes =
      installabilityData?.installability?.uninstallableReason
        ?.missingPortalScopes ?? [];
    const productSpecificMissingPortalScopes =
      appSpecific?.missingPortalInternalScopes?.flat() ?? [];
    const productSpecificMissingUserScopes =
      appSpecific?.missingUserInternalScopes?.flat() ?? [];

    const allMissingScopes = [
      ...missingUserScopes.map((scope) =>
        (scope as string).toLowerCase().replaceAll('_', '-')
      ),
      ...missingPortalScopes.map((scope) =>
        (scope as string).toLowerCase().replaceAll('_', '-')
      ),
      ...productSpecificMissingPortalScopes,
      ...productSpecificMissingUserScopes,
    ];

    // Dedupe missing scopes
    return [...new Set(allMissingScopes)];
  }, [installabilityData]);

  const missingScopeGroups = useMemo(() => {
    const productSpecific =
      installabilityData?.installability?.uninstallableReason
        ?.productSpecificUninstallableReason;
    const appSpecific =
      productSpecific?.['@type'] === 'app' ? productSpecific : undefined;

    const productSpecificMissingPortalScopeGroups =
      appSpecific?.missingPortalScopeGroups?.flat() ?? [];
    const productSpecificMissingUserScopeGroups =
      appSpecific?.missingUserScopeGroups?.flat() ?? [];

    const allMissingScopeGroups = [
      ...productSpecificMissingPortalScopeGroups,
      ...productSpecificMissingUserScopeGroups,
    ];

    // Dedupe missing scope groups
    return [...new Set(allMissingScopeGroups)];
  }, [installabilityData]);

  return {
    uninstallableReason,
    missingScopeGroups,
    missingScopes,
    loading: installabilityLoading,
    error: installabilityError,
    refetch: refetchInstallability,
  };
}
